// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useObservedWidth } from '@/billing/hooks/use-observed-width';
import type { SegmentOffsets } from '@/billing/lifecycle/aggregate-offsets';
import { buildAggregateSegmentOffsets } from '@/billing/lifecycle/aggregate-offsets';
import { formatDays } from '@/billing/lifecycle/date-helpers';
import type { GanttAxis } from '@/billing/lifecycle/gantt-math';
import {
  FALLBACK_AXIS_DAYS,
  GANTT_BAR_HEIGHT_PX,
  GANTT_BAR_VERTICAL_PADDING_PX,
  GANTT_ROW_HEIGHT_PX,
  MIN_BAR_WIDTH_PX,
  buildAxis,
  daysToPercent,
  shouldShowBarLabel,
} from '@/billing/lifecycle/gantt-math';
import { resolveBarColor } from '@/billing/lifecycle/resolve-bar-color';
import { GANTT_SEGMENT_KEYS, SEGMENTS } from '@/billing/lifecycle/segments';
import type { SegmentSummary } from '@/billing/lifecycle/transforms';
import type { IsoCurrencyCode } from '@/currency/constants/iso';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { Box, Paper, Tooltip, Typography, useTheme } from '@mui/material';
import { Decimal } from 'decimal.js';
import { useMemo } from 'react';

const EM_DASH = '—';

type OffsetsByKey = ReturnType<typeof buildAggregateSegmentOffsets>;

export function AggregateGantt(props: {
  summaries: SegmentSummary[];
  currencyCode: IsoCurrencyCode;
}) {
  const offsetsByKey = useMemo(
    () => buildAggregateSegmentOffsets(props.summaries),
    [props.summaries]
  );

  // Hide the entire chart when no segments can be placed — there's
  // nothing meaningful to show without at least one paired-anchor
  // invoice. Gating here (rather than inside the body) ensures the
  // body's `useObservedWidth` ResizeObserver only sets up once we
  // actually have a container element to observe; if the data later
  // transitions from empty to placeable, the body remounts with a
  // fresh observer rather than reusing a never-attached ref.
  const hasPlaceableSegments = GANTT_SEGMENT_KEYS.some(
    (key) => offsetsByKey[key] !== null
  );
  if (!hasPlaceableSegments) return null;

  return (
    <AggregateGanttBody
      summaries={props.summaries}
      offsetsByKey={offsetsByKey}
      currencyCode={props.currencyCode}
    />
  );
}

function AggregateGanttBody(props: {
  summaries: SegmentSummary[];
  offsetsByKey: OffsetsByKey;
  currencyCode: IsoCurrencyCode;
}) {
  const theme = useTheme();
  const [containerRef, containerWidth] = useObservedWidth();

  // Axis sizing: the chart uses only the observed range of the populated
  // segments. Leftmost anchor sits flush at 0%, rightmost at 100%; any
  // zero-width or minWidth-clamped bar spill is clipped by the container's
  // `overflow: hidden`.
  const axis = useMemo(() => {
    const dayValues: number[] = [];
    for (const key of GANTT_SEGMENT_KEYS) {
      const offsets = props.offsetsByKey[key];
      if (offsets !== null) {
        dayValues.push(offsets.startDay, offsets.endDay);
      }
    }
    if (dayValues.length === 0) {
      return { leftMostDay: 0, axisSpanDays: FALLBACK_AXIS_DAYS };
    }
    return buildAxis(Math.min(...dayValues), Math.max(...dayValues));
  }, [props.offsetsByKey]);

  // Only the segments whose (startDay, endDay) are both observed render
  // a row — no blank tracks for chain-incomplete segments.
  const placeableSegments = GANTT_SEGMENT_KEYS.flatMap((key) => {
    const offsets = props.offsetsByKey[key];
    const segmentDef = SEGMENTS.find((s) => s.key === key);
    if (offsets === null || segmentDef === undefined) return [];
    const summary = props.summaries.find((s) => s.key === key);
    const avgDays = summary?.avgDays ?? null;
    return [
      {
        key,
        offsets,
        label: segmentDef.label,
        tooltip: segmentDef.tooltip,
        color: resolveBarColor(theme, segmentDef.barColor),
        displayValue: avgDays !== null ? formatDays(avgDays) : EM_DASH,
        annualizedCarryingCost: summary?.annualizedCarryingCost ?? null,
      },
    ];
  });

  // Bottom axis "N days" — always reflects the actual observed width of
  // the chart (`axisSpanDays` = sum of the placed segment means). The
  // total_cycle mean is computed over a different invoice population
  // (only invoices with every lifecycle date present) and does not
  // equal the sum of the six per-segment means, so using it here would
  // mislabel the visible axis.
  const axisEndLabelDays = Math.round(axis.axisSpanDays);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box ref={containerRef} sx={{ position: 'relative', overflow: 'hidden' }}>
        {placeableSegments.map((seg) => (
          <GanttRow
            key={seg.key}
            label={seg.label}
            color={seg.color}
            offsets={seg.offsets}
            displayValue={seg.displayValue}
            tooltip={seg.tooltip}
            axis={axis}
            axisWidthPx={containerWidth}
            annualizedCarryingCost={seg.annualizedCarryingCost}
            currencyCode={props.currencyCode}
          />
        ))}
        {/* Connectors — 1px dotted vertical lines spanning only the
            inter-row whitespace (bottom of bar N to top of bar N+1) at
            the shared anchor x-position. Only drawn when two placeable
            rows are also adjacent in the original chain; a dropped
            middle segment correctly breaks the connector. */}
        {placeableSegments.slice(0, -1).map((seg, idx) => {
          const next = placeableSegments[idx + 1];
          if (next === undefined) return null;
          const currentChainIdx = GANTT_SEGMENT_KEYS.indexOf(seg.key);
          const nextChainIdx = GANTT_SEGMENT_KEYS.indexOf(next.key);
          if (nextChainIdx !== currentChainIdx + 1) return null;
          const xPct = daysToPercent(seg.offsets.endDay, axis);
          const topPx =
            idx * GANTT_ROW_HEIGHT_PX +
            GANTT_BAR_VERTICAL_PADDING_PX +
            GANTT_BAR_HEIGHT_PX;
          const heightPx = 2 * GANTT_BAR_VERTICAL_PADDING_PX;
          return (
            <Box
              key={`connector-${seg.key}`}
              sx={{
                position: 'absolute',
                left: `${xPct}%`,
                top: `${topPx}px`,
                width: '1px',
                height: `${heightPx}px`,
                transform: 'translateX(-50%)',
                backgroundImage: `linear-gradient(to bottom, ${theme.palette.grey[400]} 50%, transparent 50%)`,
                backgroundSize: '1px 3px',
                backgroundRepeat: 'repeat-y',
                pointerEvents: 'none',
              }}
            />
          );
        })}
      </Box>
      <Box
        sx={{
          mt: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          0 days
        </Typography>
        <Box sx={{ flex: 1, height: '1px', backgroundColor: 'text.primary' }} />
        <Typography variant="caption" color="text.secondary">
          {axisEndLabelDays} days
        </Typography>
      </Box>
    </Paper>
  );
}

interface GanttRowProps {
  label: string;
  color: string;
  offsets: SegmentOffsets;
  displayValue: string;
  tooltip: string;
  axis: GanttAxis;
  axisWidthPx: number;
  annualizedCarryingCost: Decimal | null;
  currencyCode: IsoCurrencyCode;
}

// A single Gantt row — background track plus an absolutely-positioned
// bar at (leftPct%, widthPct%). Only called for placeable segments;
// the parent filters out chain-incomplete rows entirely.
function GanttRow(props: GanttRowProps) {
  const {
    label,
    color,
    offsets,
    displayValue,
    tooltip,
    axis,
    axisWidthPx,
    annualizedCarryingCost,
    currencyCode,
  } = props;
  const leftPct = daysToPercent(
    Math.min(offsets.startDay, offsets.endDay),
    axis
  );
  const widthPct =
    daysToPercent(Math.max(offsets.startDay, offsets.endDay), axis) - leftPct;
  const showLabel = shouldShowBarLabel(widthPct, axisWidthPx, displayValue);
  return (
    <Box
      sx={{
        position: 'relative',
        height: GANTT_BAR_HEIGHT_PX + 8,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          height: GANTT_BAR_HEIGHT_PX,
          backgroundColor: 'action.hover',
        }}
      >
        <Tooltip
          title={
            <>
              {label}: {displayValue}
              <br />
              {annualizedCarryingCost === null ? (
                <>Annualized carrying cost: {EM_DASH}</>
              ) : (
                <>
                  Annualized carrying{' '}
                  {annualizedCarryingCost.lessThan(0) ? 'gain' : 'cost'}:{' '}
                  {formatCurrencyValue({
                    amount: annualizedCarryingCost.abs(),
                    isoCode: currencyCode,
                  })}
                </>
              )}
              <br />
              <br />
              {tooltip}
            </>
          }
          arrow
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${leftPct}%`,
              width: `${widthPct}%`,
              minWidth: `${MIN_BAR_WIDTH_PX}px`,
              backgroundColor: color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {showLabel && (
              <Typography
                variant="caption"
                sx={{
                  color: 'common.white',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  px: 0.5,
                }}
              >
                {displayValue}
              </Typography>
            )}
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
}
